import { useState, useEffect } from 'react';
import {
  Plus,
  Copy,
  Trash2,
  Check,
  X,
  Edit2,
  Key,
  RefreshCw,
  Zap,
  Clock,
  Coins,
  Server,
  Cpu,
  AlertCircle,
  Shield,
  ShieldCheck,
  SlidersHorizontal,
} from 'lucide-react';
import { keys, ApiKey, CreateKeyRequest, UpdateKeyRequest, providersApi, Provider } from '../api/client';
import { formatDateTime, formatNumber, formatCurrency, cn } from '../lib/utils';

type ReasoningOverrideSelection = 'default' | 'on' | 'off';

export default function ApiKeys() {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingKey, setEditingKey] = useState<ApiKey | null>(null);
  const [newKeyValue, setNewKeyValue] = useState<string | null>(null);
  const [copiedKeyId, setCopiedKeyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      const [keysRes, providersRes] = await Promise.all([
        keys.list(),
        providersApi.list(),
      ]);
      setApiKeys(keysRes.keys);
      setProviders(providersRes.providers);
      setError(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch data';
      console.error('Failed to fetch data:', error);
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateKey = async (data: CreateKeyRequest) => {
    try {
      setError(null);
      const response = await keys.create(data);
      setNewKeyValue(response.key.apiKey);
      setShowCreateModal(false);
      await fetchData();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create key';
      console.error('Failed to create key:', error);
      setError(message);
    }
  };

  const handleUpdateKey = async (id: string, data: UpdateKeyRequest) => {
    try {
      setError(null);
      await keys.update(id, data);
      setEditingKey(null);
      await fetchData();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update key';
      console.error('Failed to update key:', error);
      setError(message);
    }
  };

  const handleToggleActive = async (key: ApiKey) => {
    try {
      await keys.update(key.id, { isActive: !key.isActive });
      await fetchData();
    } catch (error) {
      console.error('Failed to toggle key:', error);
    }
  };

  const handleDeleteKey = async (key: ApiKey) => {
    if (!confirm(`Are you sure you want to delete the API key for "${key.appName}"?`)) {
      return;
    }

    try {
      await keys.delete(key.id);
      await fetchData();
    } catch (error) {
      console.error('Failed to delete key:', error);
    }
  };

  const copyToClipboard = async (text: string, keyId?: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedKeyId(keyId || 'new');
    setTimeout(() => setCopiedKeyId(null), 2000);
  };

  const getProviderName = (providerId: string | null) => {
    if (!providerId) return '-';
    const provider = providers.find(p => p.id === providerId);
    return provider?.name || providerId;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-3 text-muted-foreground">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Loading API keys...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-in-up">
        <div>
          <h1 className="text-3xl font-bold text-gradient">API Keys</h1>
          <p className="text-muted-foreground mt-1">Manage application access tokens</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-glow px-4 py-2.5 rounded-xl flex items-center gap-2 text-sm"
        >
          <span className="flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Create Key
          </span>
        </button>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-5 animate-scale-in">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-rose-500/20 flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-5 h-5 text-rose-400" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-rose-400 mb-1">Error</p>
              <p className="text-sm text-rose-300/80">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="p-2 rounded-lg bg-rose-500/10 text-rose-400 hover:bg-rose-500/20 transition-all"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* New Key Created Alert */}
      {newKeyValue && (
        <div className="alert-success rounded-xl p-5 animate-scale-in">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
              <Key className="w-5 h-5 text-emerald-400" />
            </div>
            <div className="flex-1">
              <p className="font-medium mb-2">API key created successfully!</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 code-text px-4 py-3 rounded-lg font-mono text-sm break-all">
                  {newKeyValue}
                </code>
                <button
                  onClick={() => copyToClipboard(newKeyValue)}
                  className={cn(
                    'p-3 rounded-lg transition-all',
                    copiedKeyId === 'new'
                      ? 'bg-emerald-500/20 text-emerald-400'
                      : 'bg-secondary text-muted-foreground hover:text-foreground'
                  )}
                >
                  {copiedKeyId === 'new' ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                </button>
                <button
                  onClick={() => setNewKeyValue(null)}
                  className="p-3 rounded-lg bg-secondary text-muted-foreground hover:text-foreground transition-all"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Keys Table */}
      <div className="card-glow rounded-2xl overflow-hidden animate-fade-in-up animate-delay-100">
        <table className="table-neural min-w-full">
          <thead>
            <tr>
              <th className="px-6 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Application
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Provider / Model
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Security
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Usage Today
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Created
              </th>
              <th className="px-6 py-4 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {apiKeys.map((key, index) => (
              <tr
                key={key.id}
                className="animate-fade-in-up"
                style={{ animationDelay: `${(index + 2) * 50}ms` }}
              >
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
                      <Key className="w-4 h-4 text-cyan-400" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium">{key.appName}</div>
                      <div className="flex items-center gap-2 mt-1">
                        <code className="text-xs text-muted-foreground font-mono truncate max-w-[200px]" title={key.fullKey || `sk-proxy-${key.keyPrefix}...`}>
                          {key.fullKey || `sk-proxy-${key.keyPrefix}...`}
                        </code>
                        {key.fullKey && (
                          <button
                            onClick={() => copyToClipboard(key.fullKey!, key.id)}
                            className={cn(
                              'p-1 rounded transition-all flex-shrink-0',
                              copiedKeyId === key.id
                                ? 'bg-emerald-500/20 text-emerald-400'
                                : 'bg-secondary text-muted-foreground hover:text-cyan-400'
                            )}
                            title="Copy API key"
                          >
                            {copiedKeyId === key.id ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <Server className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm">{getProviderName(key.defaultProvider)}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <Cpu className="w-3.5 h-3.5 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground font-mono">
                      {key.forcedModel || <span className="italic">No model set</span>}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => handleToggleActive(key)}
                    className={cn(
                      'px-3 py-1.5 text-xs font-medium rounded-lg transition-all',
                      key.isActive ? 'badge-active' : 'badge-inactive'
                    )}
                  >
                    {key.isActive ? 'Active' : 'Inactive'}
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    {key.requireAttestation ? (
                      <>
                        <ShieldCheck className="w-4 h-4 text-emerald-400" />
                        <span className="text-xs font-medium text-emerald-400">App Attest</span>
                      </>
                    ) : (
                      <>
                        <Shield className="w-4 h-4 text-muted-foreground/50" />
                        <span className="text-xs text-muted-foreground/50">None</span>
                      </>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-amber-400" />
                    <span className="text-sm font-mono">
                      {formatNumber(key.usage?.tokensToday ?? 0)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <Coins className="w-3.5 h-3.5 text-emerald-400" />
                    <span className="text-xs text-muted-foreground">
                      {formatCurrency(key.usage?.costToday ?? 0)}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="w-4 h-4" />
                    {formatDateTime(key.createdAt)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => setEditingKey(key)}
                      className="p-2 rounded-lg bg-secondary text-muted-foreground hover:text-cyan-400 transition-colors"
                      title="Edit"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteKey(key)}
                      className="p-2 rounded-lg bg-secondary text-muted-foreground hover:text-rose-400 transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {apiKeys.length === 0 && (
              <tr>
                <td colSpan={7} className="px-6 py-16 text-center">
                  <div className="flex flex-col items-center gap-4">
                    <div className="w-16 h-16 rounded-2xl bg-secondary flex items-center justify-center">
                      <Key className="w-8 h-8 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-muted-foreground mb-1">No API keys yet</p>
                      <p className="text-sm text-muted-foreground/70">Create one to get started</p>
                    </div>
                    <button
                      onClick={() => setShowCreateModal(true)}
                      className="btn-secondary px-4 py-2 rounded-lg text-sm"
                    >
                      Create your first key
                    </button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Create Key Modal */}
      {showCreateModal && (
        <KeyModal
          providers={providers}
          onClose={() => setShowCreateModal(false)}
          onSubmit={(data) => handleCreateKey(data as CreateKeyRequest)}
        />
      )}

      {/* Edit Key Modal */}
      {editingKey && (
        <KeyModal
          providers={providers}
          existingKey={editingKey}
          onClose={() => setEditingKey(null)}
          onSubmit={(data) => handleUpdateKey(editingKey.id, data)}
        />
      )}
    </div>
  );
}

function KeyModal({
  providers,
  existingKey,
  onClose,
  onSubmit,
}: {
  providers: Provider[];
  existingKey?: ApiKey;
  onClose: () => void;
  onSubmit: (data: CreateKeyRequest | UpdateKeyRequest) => void;
}) {
  const isEditing = !!existingKey;

  const [appName, setAppName] = useState(existingKey?.appName || '');
  const [defaultProvider, setDefaultProvider] = useState(existingKey?.defaultProvider || '');
  const [forcedModel, setForcedModel] = useState(existingKey?.forcedModel || '');
  const [requireAttestation, setRequireAttestation] = useState(existingKey?.requireAttestation ?? false);
  const [frequencyPenalty, setFrequencyPenalty] = useState<string>(
    existingKey?.frequencyPenalty != null ? String(existingKey.frequencyPenalty) : ''
  );
  const [presencePenalty, setPresencePenalty] = useState<string>(
    existingKey?.presencePenalty != null ? String(existingKey.presencePenalty) : ''
  );
  const [temperatureOverride, setTemperatureOverride] = useState<string>(
    existingKey?.temperatureOverride != null ? String(existingKey.temperatureOverride) : ''
  );
  const [reasoningOverride, setReasoningOverride] = useState<ReasoningOverrideSelection>(
    existingKey?.reasoningOverride === 1
      ? 'on'
      : existingKey?.reasoningOverride === 0
        ? 'off'
        : 'default'
  );

  // Get models for selected provider (use enabledModels which includes custom models)
  const selectedProvider = providers.find(p => p.id === defaultProvider);
  const availableModels = selectedProvider?.enabledModels || [];

  const parsePenalty = (value: string): number | null => {
    if (value.trim() === '') return null;
    const num = parseFloat(value);
    return isNaN(num) ? null : num;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const reasoningOverrideValue = reasoningOverride === 'on'
      ? 1
      : reasoningOverride === 'off'
        ? 0
        : null;

    if (isEditing) {
      const data: UpdateKeyRequest = {};
      if (appName !== existingKey.appName) data.appName = appName;
      data.defaultProvider = defaultProvider || null;
      data.forcedModel = forcedModel || null;
      data.requireAttestation = requireAttestation;
      data.frequencyPenalty = parsePenalty(frequencyPenalty);
      data.presencePenalty = parsePenalty(presencePenalty);
      data.temperatureOverride = parsePenalty(temperatureOverride);
      data.reasoningOverride = reasoningOverrideValue;
      onSubmit(data);
    } else {
      const data: CreateKeyRequest = { appName };
      if (defaultProvider) data.defaultProvider = defaultProvider;
      if (forcedModel) data.forcedModel = forcedModel;
      data.requireAttestation = requireAttestation;
      data.frequencyPenalty = parsePenalty(frequencyPenalty);
      data.presencePenalty = parsePenalty(presencePenalty);
      data.temperatureOverride = parsePenalty(temperatureOverride);
      data.reasoningOverride = reasoningOverrideValue;
      onSubmit(data);
    }
  };

  return (
    <div className="fixed inset-0 modal-overlay flex items-center justify-center z-50 p-4">
      <div
        className="modal-content rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center">
            {isEditing ? (
              <Edit2 className="w-5 h-5 text-cyan-400" />
            ) : (
              <Key className="w-5 h-5 text-cyan-400" />
            )}
          </div>
          <div>
            <h2 className="text-lg font-semibold">
              {isEditing ? 'Edit API Key' : 'Create API Key'}
            </h2>
            <p className="text-sm text-muted-foreground">
              {isEditing ? 'Update key configuration' : 'Configure a new access token'}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* App Name */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              App Name
            </label>
            <input
              type="text"
              required={!isEditing}
              value={appName}
              onChange={(e) => setAppName(e.target.value)}
              className="input-neural w-full px-4 py-3 rounded-xl text-sm"
              placeholder="My iOS App"
            />
          </div>

          {/* Provider and Model Selection */}
          <div className="card-glow rounded-xl p-5 space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <Server className="w-4 h-4 text-violet-400" />
              <span className="text-sm font-medium">Provider Configuration</span>
            </div>

            <div>
              <label className="block text-sm text-muted-foreground mb-2">Provider</label>
              <select
                value={defaultProvider}
                onChange={(e) => {
                  setDefaultProvider(e.target.value);
                  setForcedModel('');
                }}
                className="input-neural w-full px-4 py-3 rounded-xl text-sm appearance-none cursor-pointer"
              >
                <option value="">Select a provider...</option>
                {providers.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm text-muted-foreground mb-2">Model</label>
              {defaultProvider ? (
                availableModels.length > 0 ? (
                  <select
                    value={forcedModel}
                    onChange={(e) => setForcedModel(e.target.value)}
                    className="input-neural w-full px-4 py-3 rounded-xl text-sm appearance-none cursor-pointer"
                  >
                    <option value="">Select a model...</option>
                    {availableModels.map((model) => (
                      <option key={model} value={model}>
                        {model}
                      </option>
                    ))}
                  </select>
                ) : selectedProvider?.supportsAllModels ? (
                  <input
                    type="text"
                    value={forcedModel}
                    onChange={(e) => setForcedModel(e.target.value)}
                    className="input-neural w-full px-4 py-3 rounded-xl text-sm"
                    placeholder="Enter model name (e.g., claude-3-opus)"
                  />
                ) : (
                  <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-secondary text-muted-foreground text-sm">
                    <AlertCircle className="w-4 h-4" />
                    <span>No models available — add models in Settings</span>
                  </div>
                )
              ) : (
                <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-secondary text-muted-foreground text-sm">
                  <AlertCircle className="w-4 h-4" />
                  <span>Select a provider first</span>
                </div>
              )}
            </div>

            <p className="text-xs text-muted-foreground">
              The selected provider and model will be used for all requests from this API key.
              iOS apps don't need to specify a model - it will be automatically set.
            </p>
          </div>

          {/* Sampling Parameters */}
          <div className="card-glow rounded-xl p-5 space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <SlidersHorizontal className="w-4 h-4 text-amber-400" />
              <span className="text-sm font-medium">Sampling Parameters</span>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-muted-foreground mb-2">Frequency Penalty</label>
                <input
                  type="number"
                  step="0.05"
                  min="-2"
                  max="2"
                  value={frequencyPenalty}
                  onChange={(e) => setFrequencyPenalty(e.target.value)}
                  className="input-neural w-full px-4 py-3 rounded-xl text-sm"
                  placeholder="Not set"
                />
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-2">Presence Penalty</label>
                <input
                  type="number"
                  step="0.05"
                  min="-2"
                  max="2"
                  value={presencePenalty}
                  onChange={(e) => setPresencePenalty(e.target.value)}
                  className="input-neural w-full px-4 py-3 rounded-xl text-sm"
                  placeholder="Not set"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm text-muted-foreground mb-2">Temperature Override</label>
                <input
                  type="number"
                  step="0.05"
                  min="0"
                  max="2"
                  value={temperatureOverride}
                  onChange={(e) => setTemperatureOverride(e.target.value)}
                  className="input-neural w-full px-4 py-3 rounded-xl text-sm"
                  placeholder="Not set — use client value"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm text-muted-foreground mb-2">Thinking</label>
                <select
                  value={reasoningOverride}
                  onChange={(e) => setReasoningOverride(e.target.value as ReasoningOverrideSelection)}
                  className="input-neural w-full px-4 py-3 rounded-xl text-sm appearance-none cursor-pointer"
                >
                  <option value="default">Default</option>
                  <option value="on">On</option>
                  <option value="off">Off</option>
                </select>
                <p className="text-xs text-muted-foreground mt-2">
                  OpenRouter only. Default leaves the request untouched (whatever the app sends, or the
                  model's own behavior). 'Off' is rejected by always-thinking models (e.g. DeepSeek R1).
                </p>
              </div>
            </div>

            <p className="text-xs text-muted-foreground">
              Frequency / Presence: repetition control (-2.0 to 2.0). Frequency penalty reduces repeated tokens proportionally;
              presence penalty discourages any token that already appeared.
              Temperature Override (0–2): replaces the client-sent temperature for every request from this key. Leave empty to honour what the client sends.
            </p>
          </div>

          {/* Security */}
          <div className="card-glow rounded-xl p-5 space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="w-4 h-4 text-emerald-400" />
              <span className="text-sm font-medium">Security</span>
            </div>

            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <div className="text-sm font-medium">Require App Attest</div>
                <p className="text-xs text-muted-foreground mt-1">
                  Only accept requests from devices verified via Apple App Attest.
                  Prevents unauthorized API usage from non-app clients.
                </p>
              </div>
              <div className="relative ml-4 flex-shrink-0">
                <input
                  type="checkbox"
                  checked={requireAttestation}
                  onChange={(e) => setRequireAttestation(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 rounded-full bg-secondary peer-checked:bg-emerald-500/30 transition-colors" />
                <div className="absolute left-0.5 top-0.5 w-5 h-5 rounded-full bg-muted-foreground peer-checked:bg-emerald-400 peer-checked:translate-x-5 transition-all" />
              </div>
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary px-5 py-2.5 rounded-xl text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-glow px-5 py-2.5 rounded-xl text-sm"
            >
              <span>{isEditing ? 'Save Changes' : 'Create Key'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
